import { useInfiniteQuery } from "@tanstack/react-query";
import {
  recordService,
  RecordWithTemplate,
} from "@/integrations/records/recordService";
import { Database } from "@/integrations/supabase/types";

type ProcessingStatus = Database["public"]["Enums"]["processing_status"];
type SortBy = "newest" | "oldest" | "name-asc" | "name-desc";

interface UseInfiniteRecordsOptions {
  enabled?: boolean;
  userId: string;
  limit?: number;
  search?: string;
  statusFilter?: ProcessingStatus | "all";
  sortBy?: SortBy;
  sortDirection?: "asc" | "desc";
  selectedTags?: string[];
}

export const useInfiniteRecords = ({
  enabled = true,
  userId,
  limit = 20,
  search = "",
  statusFilter = "all",
  sortBy = "newest",
  sortDirection = "desc",
  selectedTags = [],
}: UseInfiniteRecordsOptions) => {
  // Early validation
  if (!userId) {
    throw new Error("useInfiniteRecords: userId is required");
  }

  const query = useInfiniteQuery({
    queryKey: ["records-infinite", { userId, limit, search, statusFilter, sortBy, sortDirection, selectedTags }],
    queryFn: ({ pageParam = 1 }) => {
      return recordService.getPaginatedRecordsWithTags({
        page: pageParam,
        limit,
        search,
        statusFilter,
        sortBy,
        sortDirection,
        userId,
        tagFilter: selectedTags.length > 0 ? selectedTags : undefined,
      });
    },
    getNextPageParam: (lastPage) => {
      return lastPage.pagination.hasNextPage 
        ? lastPage.pagination.page + 1 
        : undefined;
    },
    enabled,
    staleTime: 30000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnMount: true,
    initialPageParam: 1,
  });

  return {
    ...query,
    // Flatten all pages into a single array
    records: query.data?.pages.flatMap(page => page.data) ?? [],
    // Get total records from first page
    totalRecords: query.data?.pages[0]?.pagination.total ?? 0,
  };
};

// Re-export types for backward compatibility
export type { RecordWithTemplate };